/**
 * The inputs of an environment's fields in a form (a preset, the Add node window): a picker for
 * a choice, a line for text, a box of lines for long text and code. Plain props, no service:
 * the form says which fields, the values, and how choices are listed and added.
 */
import { For, Match, Switch, type JSX } from "solid-js";
import type { FieldDef } from "@protocol/FieldDef";
import { valueOf, withValue } from "./fields";
import { ChoicePicker } from "./ui";

/** One input per field, each in a labelled block of the form. */
export function FieldInputs(props: {
  /** Prefix of the inputs' ids (`preset` → `#preset-field-language`). */
  idPrefix: string;
  /** The fields asked for. */
  fields: readonly FieldDef[];
  /** The values so far, by field id. */
  data: { [key in string]: string } | undefined;
  /** The choices offered for a choice field. */
  choices: (field: FieldDef) => string[];
  /** Adds a choice typed by the person; returns the spelling kept. */
  onAdd: (id: string, value: string) => string;
  /** The values after a change (an emptied field taken out). */
  onChange: (data: { [key in string]: string }) => void;
  /** Leave out long text and code (a small form). */
  short?: boolean;
}): JSX.Element {
  /** Sets one value. */
  const set = (id: string, value: string) => props.onChange(withValue(props.data, id, value));
  const id = (field: FieldDef) => `${props.idPrefix}-field-${field.id}`;
  return (
    <For each={props.fields.filter((f) => !props.short || f.kind === "choice" || f.kind === "text")}>
      {(field) => (
        <div class="fl" data-field={field.id} title={field.summary || undefined}>
          {field.label}
          <Switch>
            <Match when={field.kind === "choice"}>
              <ChoicePicker
                id={id(field)}
                label={field.label}
                value={valueOf({ data: props.data }, field.id)}
                blank={`No ${field.label.toLowerCase()}`}
                choices={props.choices(field)}
                onAdd={(value) => props.onAdd(field.id, value)}
                onChange={(value) => set(field.id, value)}
              />
            </Match>
            <Match when={field.kind === "text"}>
              <input class="field" id={id(field)} maxlength="200" value={valueOf({ data: props.data }, field.id)} onInput={(e) => set(field.id, e.currentTarget.value)} />
            </Match>
            <Match when={true}>
              <textarea
                class={field.kind === "code" ? "codeta" : "field"}
                id={id(field)}
                rows="5"
                maxlength="4000"
                spellcheck={field.kind !== "code"}
                value={valueOf({ data: props.data }, field.id)}
                onInput={(e) => set(field.id, e.currentTarget.value)}
              />
            </Match>
          </Switch>
        </div>
      )}
    </For>
  );
}
